/**
 * Localized job-change phrases injected into the AND row when the
 * "Listen for job change announcements" toggle is enabled.
 *
 * These are LinkedIn search terms (not UI copy), so they live here as a data
 * constant rather than in the i18n translation files.
 */
export const LANGUAGE_PHRASES = {
	en: "I'm starting a new position as",
	de: "Ich habe einen neuen job als",
	fr: "Je commence un nouveau poste en tant que",
	es: "Empiezo un nuevo puesto como",
	it: "Inizio una nuova posizione come",
	nl: "Ik begin een nieuwe functie als",
	pt: "Começo uma nova posição como",
} as const;

export type JobChangeLanguage = keyof typeof LANGUAGE_PHRASES;

/** Default language for the job-change phrase picker. */
export const DEFAULT_JOB_CHANGE_LANGUAGE: JobChangeLanguage = "en";

/** English display labels for each language option, shown in the picker. */
export const LANGUAGE_LABELS: Record<JobChangeLanguage, string> = {
	en: "English",
	de: "German",
	fr: "French",
	es: "Spanish",
	it: "Italian",
	nl: "Dutch",
	pt: "Portuguese",
};

/** Ordered list of selectable languages (matches the mockup). */
export const JOB_CHANGE_LANGUAGES: JobChangeLanguage[] = ["en", "de", "fr", "es", "it", "nl", "pt"];

/**
 * Returns the language whose phrase matches `value` (case-insensitive), or
 * null when the value is not a known job-change phrase.
 */
export function findLanguageByPhrase(value: string): JobChangeLanguage | null {
	const normalized = value.trim().toLowerCase();

	const match = (Object.keys(LANGUAGE_PHRASES) as JobChangeLanguage[]).find(
		(lang) => LANGUAGE_PHRASES[lang].toLowerCase() === normalized
	);

	return match ?? null;
}
